import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiError, uploadImages } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageParts";
import { JudgeBadge, RoleBadge } from "@/components/common/Badges";
import { ConnectedApps } from "@/components/settings/ConnectedApps";
import { LinkedAccounts } from "@/components/settings/LinkedAccounts";
import { UsernameRequest } from "@/components/settings/UsernameRequest";

function ProfileForm() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ display_name: user.display_name, bio: user.bio || "", website_url: user.website_url || "" });
  const [busy, setBusy] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/me", patch);
      setUser(data);
      qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  const avatar = async (file) => {
    try {
      const [res] = await uploadImages([file], "avatar");
      if (!res.ok) throw new Error(res.error);
      await save({ avatar_file_id: res.file_id });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <form className="panel space-y-5 p-6" onSubmit={(e) => { e.preventDefault(); save({ ...form, website_url: form.website_url || null }); }} data-testid="profile-form">
      <div className="flex items-center gap-5">
        {user.avatar_url ? <img src={assetUrl(user.avatar_url)} alt="" className="h-20 w-20 rounded-xl object-cover" data-testid="settings-avatar" />
          : <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-amber-900/30 font-display text-3xl text-amber-200">{user.display_name[0]}</div>}
        <label className="btn-outline cursor-pointer" data-testid="settings-avatar-label">
          Change avatar
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="settings-avatar-input" onChange={(e) => e.target.files?.[0] && avatar(e.target.files[0])} />
        </label>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="s-name">Display name</label>
          <input id="s-name" required maxLength={60} className="field" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="settings-display-name-input" />
          <p className="mt-1 text-xs text-slate-500">Shown on your profile and cards. Change it any time.</p>
        </div>
        <UsernameRequest />
      </div>
      <div>
        <label className="field-label" htmlFor="s-bio">Bio</label>
        <textarea id="s-bio" rows={4} maxLength={1000} className="field" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="settings-bio-input" />
      </div>
      <div>
        <label className="field-label" htmlFor="s-web">Website</label>
        <input id="s-web" type="url" placeholder="https://" className="field" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} data-testid="settings-website-input" />
      </div>
      <button type="submit" className="btn-gold" disabled={busy} data-testid="settings-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save profile</button>
    </form>
  );
}

function PasswordForm() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", form);
      toast.success(user.has_password ? "Password changed" : "Password set — you can now also sign in with email");
      setForm({ current_password: "", new_password: "" });
      refreshUser();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form className="panel space-y-4 p-6" onSubmit={submit} data-testid="password-form">
      <p className="eyebrow">{user.has_password ? "Change password" : "Set a password"}</p>
      {user.has_password && (
        <div>
          <label className="field-label" htmlFor="pw-current">Current password</label>
          <input id="pw-current" type="password" required className="field" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="settings-current-password-input" />
        </div>
      )}
      <div>
        <label className="field-label" htmlFor="pw-new">New password</label>
        <input id="pw-new" type="password" minLength={8} required className="field" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="settings-new-password-input" />
      </div>
      <button type="submit" className="btn-outline" data-testid="settings-password-submit"><KeyRound className="h-4 w-4" />Update password</button>
    </form>
  );
}

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Account" title="Settings" testid="settings-header" />
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-8"><ProfileForm /><ConnectedApps /><LinkedAccounts /></div>
        <div className="space-y-6 lg:col-span-4">
          <div className="panel space-y-3 p-6 text-sm" data-testid="account-summary">
            <p className="eyebrow">Account</p>
            <p className="text-slate-300">{user.email}</p>
            <div className="flex flex-wrap gap-1"><RoleBadge role={user.role} /><JudgeBadge level={user.judge_level} /></div>
            <p className="text-slate-500">Sign-in methods: {user.auth_providers.map((p) => (p === "google" ? "Google" : "Email & password")).join(", ")}</p>
          </div>
          <PasswordForm />
        </div>
      </div>
    </div>
  );
}
